/**
 * Offline Storage Service
 * Uses IndexedDB for storing data offline (messages, conversations)
 */

const DB_NAME = 'lms-offline-db';
const DB_VERSION = 1;

// Store names
const STORES = {
  CONVERSATIONS: 'conversations',
  MESSAGES: 'messages',
  PENDING_MESSAGES: 'pending-messages',
  USER_DATA: 'user-data',
};

let db = null;

/**
 * Initialize the IndexedDB database
 */
export async function initDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('Failed to open IndexedDB:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      console.log('IndexedDB initialized');
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;

      // Conversations store
      if (!database.objectStoreNames.contains(STORES.CONVERSATIONS)) {
        const conversationStore = database.createObjectStore(STORES.CONVERSATIONS, { keyPath: 'id' });
        conversationStore.createIndex('participantId', 'participantId', { unique: false });
        conversationStore.createIndex('updatedAt', 'updatedAt', { unique: false });
      }

      // Messages store
      if (!database.objectStoreNames.contains(STORES.MESSAGES)) {
        const messageStore = database.createObjectStore(STORES.MESSAGES, { keyPath: 'id' });
        messageStore.createIndex('conversationId', 'conversationId', { unique: false });
        messageStore.createIndex('timestamp', 'timestamp', { unique: false });
      }

      // Pending messages store (for offline sync)
      if (!database.objectStoreNames.contains(STORES.PENDING_MESSAGES)) {
        const pendingStore = database.createObjectStore(STORES.PENDING_MESSAGES, { keyPath: 'tempId', autoIncrement: true });
        pendingStore.createIndex('conversationId', 'conversationId', { unique: false });
        pendingStore.createIndex('createdAt', 'createdAt', { unique: false });
      }

      // User data store
      if (!database.objectStoreNames.contains(STORES.USER_DATA)) {
        database.createObjectStore(STORES.USER_DATA, { keyPath: 'key' });
      }

      console.log('IndexedDB schema created/updated');
    };
  });
}

/**
 * Get the database instance
 */
async function getDB() {
  if (!db) {
    await initDB();
  }
  return db;
}

/**
 * Generic add/update function
 */
async function put(storeName, data) {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.put(data);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Generic get function
 */
async function get(storeName, key) {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.get(key);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Generic getAll function
 */
async function getAll(storeName) {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Generic delete function
 */
async function remove(storeName, key) {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.delete(key);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Clear a store
 */
async function clearStore(storeName) {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.clear();

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

// ===== Conversation Operations =====

export async function saveConversation(conversation) {
  return put(STORES.CONVERSATIONS, {
    ...conversation,
    updatedAt: Date.now(),
  });
}

export async function getConversation(id) {
  return get(STORES.CONVERSATIONS, id);
}

export async function getAllConversations() {
  return getAll(STORES.CONVERSATIONS);
}

/**
 * Make the store hold exactly this list — one transaction, so a reader never
 * sees the gap between the clear and the writes.
 */
export async function replaceConversations(conversations) {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORES.CONVERSATIONS, 'readwrite');
    const store = transaction.objectStore(STORES.CONVERSATIONS);
    store.clear();
    conversations.forEach((conversation) => {
      store.put({ ...conversation, updatedAt: Date.now() });
    });

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
}

export async function deleteConversation(id) {
  return remove(STORES.CONVERSATIONS, id);
}

// ===== Message Operations =====

export async function saveMessage(message) {
  return put(STORES.MESSAGES, message);
}

export async function saveMessages(messages) {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORES.MESSAGES, 'readwrite');
    const store = transaction.objectStore(STORES.MESSAGES);

    messages.forEach(message => {
      store.put(message);
    });

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
}

export async function getMessagesByConversation(conversationId) {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORES.MESSAGES, 'readonly');
    const store = transaction.objectStore(STORES.MESSAGES);
    const index = store.index('conversationId');
    const request = index.getAll(conversationId);

    request.onsuccess = () => {
      const messages = request.result || [];
      // Oldest first. Server messages carry createdAt; timestamp is kept for
      // anything written by an older build.
      messages.sort(
        (a, b) => new Date(a.createdAt || a.timestamp) - new Date(b.createdAt || b.timestamp)
      );
      resolve(messages);
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Make the store hold exactly these messages for one conversation, leaving
 * every other conversation's messages alone.
 */
export async function replaceMessagesForConversation(conversationId, messages) {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORES.MESSAGES, 'readwrite');
    const store = transaction.objectStore(STORES.MESSAGES);
    const index = store.index('conversationId');

    const cursorRequest = index.openKeyCursor(IDBKeyRange.only(conversationId));
    cursorRequest.onsuccess = () => {
      const cursor = cursorRequest.result;
      if (cursor) {
        store.delete(cursor.primaryKey);
        cursor.continue();
        return;
      }
      // Deletes are queued; puts land after them in the same transaction.
      messages.forEach((message) => {
        store.put({ ...message, conversationId });
      });
    };

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
}

export async function deleteMessagesByConversation(conversationId) {
  const messages = await getMessagesByConversation(conversationId);
  const database = await getDB();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORES.MESSAGES, 'readwrite');
    const store = transaction.objectStore(STORES.MESSAGES);

    messages.forEach(message => {
      store.delete(message.id);
    });

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
}

// ===== Pending Message Operations (for offline sync) =====

export async function savePendingMessage(message) {
  return put(STORES.PENDING_MESSAGES, {
    ...message,
    tempId: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    createdAt: Date.now(),
    status: 'pending',
  });
}

export async function getAllPendingMessages() {
  return getAll(STORES.PENDING_MESSAGES);
}

export async function deletePendingMessage(tempId) {
  return remove(STORES.PENDING_MESSAGES, tempId);
}

export async function clearPendingMessages() {
  return clearStore(STORES.PENDING_MESSAGES);
}

// ===== User Data Operations =====

export async function saveUserData(key, value) {
  return put(STORES.USER_DATA, { key, value });
}

export async function getUserData(key) {
  const result = await get(STORES.USER_DATA, key);
  return result?.value;
}

export async function deleteUserData(key) {
  return remove(STORES.USER_DATA, key);
}

// ===== Utility Functions =====

export async function clearAllData() {
  await clearStore(STORES.CONVERSATIONS);
  await clearStore(STORES.MESSAGES);
  await clearStore(STORES.PENDING_MESSAGES);
  await clearStore(STORES.USER_DATA);
  console.log('All offline data cleared');
}

export function isOnline() {
  return navigator.onLine;
}

// Listen for online/offline events
export function setupNetworkListeners(onOnline, onOffline) {
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);

  return () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
  };
}

